/**
 * Sign-in methods panel — lets the signed-in user LINK additional Firebase
 * auth providers (Google / Phone / Email link) to their existing account.
 * All three methods eventually sign the user in to the same Social Pulse
 * user, so linking prevents duplicate accounts across providers.
 *
 * The heavy lifting lives in /app/frontend/src/lib/firebase.js — this file
 * is just the UI + error-handling.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Chrome, Mail, Phone as PhoneIcon, Check, X, Loader2, ShieldCheck, Info, Trash2,
} from "lucide-react";
import {
  firebaseAuth,
  listLinkedProviders,
  linkGoogleProvider,
  startLinkPhone,
  confirmLinkPhone,
  sendEmailLinkForLinking,
  isEmailLinkForLinkingCallback,
  readStoredEmailForLinking,
  completeEmailLinkLinking,
  unlinkProvider,
  reauthenticateGoogle,
  accountLinkErrorMessage,
  PROVIDER_LABELS,
} from "@/lib/firebase";

const PROVIDER_META = {
  "google.com": { icon: Chrome,     label: "Google",     detail: (p) => p.email || "" },
  "phone":      { icon: PhoneIcon,  label: "Phone",      detail: (p) => p.phoneNumber || "" },
  "password":   { icon: Mail,       label: "Email link", detail: (p) => p.email || "" },
};

export default function SignInMethodsTab() {
  const [providers, setProviders] = useState(() => listLinkedProviders());
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const [fbUser, setFbUser] = useState(() => firebaseAuth.currentUser);

  // Refresh from Firebase whenever auth state changes (e.g. after link/unlink).
  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((u) => {
      setFbUser(u);
      setProviders(listLinkedProviders());
    });
    return unsub;
  }, []);

  const has = useMemo(() => {
    const s = new Set(providers.map((p) => p.providerId));
    return {
      google: s.has("google.com"),
      phone:  s.has("phone"),
      email:  s.has("password"),
    };
  }, [providers]);

  const doLink = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
      setProviders(listLinkedProviders());
      toast.success("Sign-in method linked.");
    } catch (e) {
      if (e?.code === "auth/requires-recent-login") {
        // Reauth then retry.
        try {
          await reauthenticateGoogle();
          await fn();
          setProviders(listLinkedProviders());
          toast.success("Sign-in method linked.");
        } catch (e2) {
          toast.error(accountLinkErrorMessage(e2));
        }
      } else {
        toast.error(accountLinkErrorMessage(e));
      }
    } finally { setBusy(""); }
  };

  const doUnlink = async (providerId) => {
    const label = PROVIDER_LABELS[providerId] || providerId;
    if (!window.confirm(`Remove ${label} as a sign-in method for this account?`)) return;
    setBusy(`unlink:${providerId}`);
    try {
      await unlinkProvider(providerId);
      setProviders(listLinkedProviders());
      toast.success(`${label} removed.`);
    } catch (e) {
      toast.error(accountLinkErrorMessage(e));
    } finally { setBusy(""); }
  };

  // Complete an email-link linking flow if we landed back on ?link-email=1.
  const completedRef = useRef(false);
  useEffect(() => {
    if (completedRef.current) return;
    if (params.get("link-email") !== "1") return;
    if (!isEmailLinkForLinkingCallback()) return;
    completedRef.current = true;
    (async () => {
      setBusy("email-callback");
      try {
        const stored = readStoredEmailForLinking();
        const email = stored || window.prompt("Confirm the email you sent the link to:");
        if (!email) return;
        await completeEmailLinkLinking(email);
        setProviders(listLinkedProviders());
        toast.success("Email link added.");
      } catch (e) {
        toast.error(accountLinkErrorMessage(e));
      } finally {
        setBusy("");
        // Clean the query so it doesn't re-run on refresh.
        params.delete("link-email");
        params.delete("oobCode");
        params.delete("apiKey");
        params.delete("mode");
        setParams(params, { replace: true });
      }
    })();
  }, [params, setParams]);

  return (
    <div className="mt-8 max-w-2xl" data-testid="signin-methods-tab">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl">Sign-in methods</h2>
      </div>
      <p className="text-sm opacity-70 mb-6">
        Link Google, your phone number, and an email so you can sign in to the same
        Social Pulse account from any of them. Linking prevents duplicate accounts.
      </p>

      {!fbUser && (
        <div className="rounded-lg border p-4 flex items-start gap-3" style={{ borderColor: "#fde68a", background: "#fffbeb" }} data-testid="signin-not-firebase-notice">
          <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#b45309" }} />
          <div className="text-sm">
            <div className="font-medium">You're not signed in via Firebase in this browser.</div>
            <p className="opacity-80 mt-1">Account linking is only available when you sign in with Google, phone, or the email magic-link on this device. Sign out and sign back in through one of those methods to manage your linked sign-ins.</p>
          </div>
        </div>
      )}

      {/* invisible reCAPTCHA anchor for phone link */}
      <div id="firebase-recaptcha" />

      {fbUser && (
      <div className="space-y-3">
        <ProviderRow
          providerId="google.com"
          linked={has.google}
          linkedInfo={providers.find((p) => p.providerId === "google.com")}
          busy={busy === "google"}
          onLink={() => doLink("google", linkGoogleProvider)}
          onUnlink={() => doUnlink("google.com")}
          unlinkBusy={busy === "unlink:google.com"}
          disableUnlink={providers.length <= 1}
        />
        <PhoneRow
          linked={has.phone}
          linkedInfo={providers.find((p) => p.providerId === "phone")}
          busyKind={busy}
          setBusy={setBusy}
          onRefresh={() => setProviders(listLinkedProviders())}
          onUnlink={() => doUnlink("phone")}
          disableUnlink={providers.length <= 1}
        />
        <EmailRow
          linked={has.email}
          linkedInfo={providers.find((p) => p.providerId === "password")}
          busyKind={busy}
          setBusy={setBusy}
          onUnlink={() => doUnlink("password")}
          disableUnlink={providers.length <= 1}
        />
      </div>
      )}

      {fbUser && (
      <div className="mt-6 flex items-start gap-2 text-xs opacity-70">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          You currently have <b>{providers.length}</b> sign-in method{providers.length === 1 ? "" : "s"}. You must keep at least one — removing your last method would lock you out.
        </p>
      </div>
      )}
    </div>
  );
}


function ProviderRow({ providerId, linked, linkedInfo, busy, onLink, onUnlink, unlinkBusy, disableUnlink }) {
  const meta = PROVIDER_META[providerId];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border p-4"
      style={{ borderColor: "var(--wofy-border)", background: linked ? "rgba(139,92,246,0.03)" : "transparent" }}
      data-testid={`signin-row-${providerId}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm">{meta.label}</div>
          {linked ? (
            <div className="text-xs opacity-70 truncate">{meta.detail(linkedInfo || {}) || "Linked"}</div>
          ) : (
            <div className="text-xs opacity-70">Not linked</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {linked ? (
          <>
            <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-emerald-700"><Check className="w-3 h-3" /> Linked</span>
            {!disableUnlink && (
              <button
                type="button"
                onClick={onUnlink}
                disabled={unlinkBusy}
                className="wofy-btn-ghost text-xs px-2 py-1"
                data-testid={`signin-unlink-${providerId}`}
                title="Remove this sign-in method"
              >
                {unlinkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                <span className="ml-1">Remove</span>
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={onLink}
            disabled={busy}
            className="wofy-btn-primary text-xs px-3 py-1.5"
            data-testid={`signin-link-${providerId}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            <span>{busy ? "Linking…" : "Link"}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function PhoneRow({ linked, linkedInfo, busyKind, setBusy, onRefresh, onUnlink, disableUnlink }) {
  const [phone, setPhone] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [otp, setOtp] = useState("");
  const Icon = PROVIDER_META.phone.icon;

  const sendOtp = async () => {
    if (!phone.trim().startsWith("+")) return toast.error("Please include the country code (e.g. +1 555 123 4567).");
    setBusy("phone-send");
    try {
      const c = await startLinkPhone(phone.trim());
      setConfirm(c);
      toast.success("Verification code sent.");
    } catch (e) {
      toast.error(accountLinkErrorMessage(e));
    } finally { setBusy(""); }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,8}$/.test(otp.trim())) return toast.error("Enter the numeric code from the SMS.");
    setBusy("phone-verify");
    try {
      await confirmLinkPhone(confirm, otp.trim());
      setConfirm(null); setOtp(""); setPhone("");
      onRefresh();
      toast.success("Phone number linked.");
    } catch (e) {
      toast.error(accountLinkErrorMessage(e));
    } finally { setBusy(""); }
  };

  return (
    <div
      className="rounded-lg border p-4"
      style={{ borderColor: "var(--wofy-border)", background: linked ? "rgba(139,92,246,0.03)" : "transparent" }}
      data-testid="signin-row-phone"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm">Phone</div>
            <div className="text-xs opacity-70 truncate">
              {linked ? (linkedInfo?.phoneNumber || "Linked") : "Not linked"}
            </div>
          </div>
        </div>
        {linked && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-emerald-700"><Check className="w-3 h-3" /> Linked</span>
            {!disableUnlink && (
              <button type="button" onClick={onUnlink} className="wofy-btn-ghost text-xs px-2 py-1" data-testid="signin-unlink-phone">
                <Trash2 className="w-3 h-3" /><span className="ml-1">Remove</span>
              </button>
            )}
          </div>
        )}
      </div>

      {!linked && (
        <div className="mt-3 pl-13 sm:pl-13" style={{ paddingLeft: "3.25rem" }}>
          {!confirm ? (
            <div className="flex gap-2 flex-wrap">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="px-3 py-2 rounded-lg border bg-transparent text-sm flex-1 min-w-[220px]"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="signin-phone-input"
              />
              <button
                type="button"
                onClick={sendOtp}
                disabled={busyKind === "phone-send" || !phone}
                className="wofy-btn-primary text-xs px-3 py-1.5"
                data-testid="signin-phone-send-otp"
              >
                {busyKind === "phone-send" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                <span>Send code</span>
              </button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap items-center">
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit code"
                className="px-3 py-2 rounded-lg border bg-transparent text-sm w-32"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="signin-phone-otp"
              />
              <button
                type="button"
                onClick={verifyOtp}
                disabled={busyKind === "phone-verify" || !otp}
                className="wofy-btn-primary text-xs px-3 py-1.5"
                data-testid="signin-phone-verify"
              >
                {busyKind === "phone-verify" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                <span>Verify &amp; link</span>
              </button>
              <button type="button" onClick={() => { setConfirm(null); setOtp(""); }} className="text-xs underline opacity-70">
                Use a different number
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmailRow({ linked, linkedInfo, busyKind, setBusy, onUnlink, disableUnlink }) {
  const [email, setEmail] = useState("");
  const Icon = PROVIDER_META.password.icon;

  const send = async () => {
    const clean = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(clean)) return toast.error("Please enter a valid email address.");
    setBusy("email-send");
    try {
      await sendEmailLinkForLinking(clean);
      toast.success("Magic link sent — open it on this device to finish linking.");
      setEmail("");
    } catch (e) {
      toast.error(accountLinkErrorMessage(e));
    } finally { setBusy(""); }
  };

  return (
    <div
      className="rounded-lg border p-4"
      style={{ borderColor: "var(--wofy-border)", background: linked ? "rgba(139,92,246,0.03)" : "transparent" }}
      data-testid="signin-row-email"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm">Email link</div>
            <div className="text-xs opacity-70 truncate">
              {linked ? (linkedInfo?.email || "Linked") : "Not linked"}
            </div>
          </div>
        </div>
        {linked && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-emerald-700"><Check className="w-3 h-3" /> Linked</span>
            {!disableUnlink && (
              <button type="button" onClick={onUnlink} className="wofy-btn-ghost text-xs px-2 py-1" data-testid="signin-unlink-email">
                <Trash2 className="w-3 h-3" /><span className="ml-1">Remove</span>
              </button>
            )}
          </div>
        )}
      </div>

      {!linked && (
        <div className="mt-3" style={{ paddingLeft: "3.25rem" }}>
          <div className="flex gap-2 flex-wrap">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="px-3 py-2 rounded-lg border bg-transparent text-sm flex-1 min-w-[220px]"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="signin-email-input"
            />
            <button
              type="button"
              onClick={send}
              disabled={busyKind === "email-send" || !email}
              className="wofy-btn-primary text-xs px-3 py-1.5"
              data-testid="signin-email-send"
            >
              {busyKind === "email-send" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              <span>Send magic link</span>
            </button>
          </div>
          <p className="text-[11px] opacity-60 mt-2">
            We'll email you a one-time link. Open it on this device to finish linking your email.
          </p>
        </div>
      )}
    </div>
  );
}
